import { useMemo } from 'react';
import { Select, SelectItem, Input, Switch, Autocomplete, AutocompleteItem } from '@heroui/react';
import { resolveChannelCount, resolveDesignTraits } from '@estimator/shared';
import type { SeatCushionDesign, SeatCushionDesignType } from '@estimator/shared';
import { useUpholsteryDesigns } from '@/api/upholstery-designs';
import { toDesignTypeOptions } from '@/utils/upholstery-design-options';

interface SeatCushionDesignSelectorProps {
  design: SeatCushionDesign;
  onChange: (design: SeatCushionDesign) => void;
  sectionWidthInches: number;
  profileHeight: number;
  hardwareOptions: Array<{ id: string; name: string }>;
}

export default function SeatCushionDesignSelector({
  design,
  onChange,
  sectionWidthInches,
  profileHeight,
  hardwareOptions,
}: SeatCushionDesignSelectorProps) {
  const updateField = <K extends keyof SeatCushionDesign>(key: K, value: SeatCushionDesign[K]) => {
    onChange({ ...design, [key]: value });
  };

  const { data: designCatalog } = useUpholsteryDesigns();
  const designTypeOptions = useMemo(
    () => toDesignTypeOptions(designCatalog, design.type),
    [designCatalog, design.type],
  );

  // Field visibility comes from the catalog row, not from a hardcoded key list.
  // resolveDesignTraits falls back to the seeded map, so the eight shipped
  // designs render exactly the same fields while the query is still pending.
  const traits = useMemo(
    () => resolveDesignTraits(design.type, designCatalog),
    [design.type, designCatalog],
  );
  const isChannel = traits.has_channels;
  const isButton = traits.has_buttons;
  const isTuftedChannel = traits.is_tufted;

  // Delegates to the resolver the cost calc uses, so the preview can never
  // promise a channel the price does not include. The inline formula this
  // replaces used Math.max(1, …) and disagreed with the engine whenever the
  // channel width exceeded the dimension.
  const autoChannelCount = useMemo(
    () => resolveChannelCount(
      { ...design, channel_count_override: null },
      sectionWidthInches,
      profileHeight,
      traits,
    ),
    [design, sectionWidthInches, profileHeight, traits],
  );

  const isManualChannels = (design.channel_count_override ?? 0) > 0;
  const effectiveChannelCount = isManualChannels ? (design.channel_count_override as number) : autoChannelCount;

  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold" style={{ color: '#e67e22' }}>
        Upholstery Design
      </h3>

      <Select
        label="Design Type"
        selectedKeys={[design.type]}
        onSelectionChange={(keys) => {
          const val = Array.from(keys)[0] as SeatCushionDesignType;
          if (val) updateField('type', val);
        }}
        size="sm"
        classNames={{ trigger: 'bg-[var(--bg-panel)]' }}
      >
        {designTypeOptions.map((opt) => (
          <SelectItem key={opt.key}>{opt.label}</SelectItem>
        ))}
      </Select>

      {isChannel && (
        <div className="grid grid-cols-3 gap-3 items-end">
          <Input
            label="Channel Width"
            type="number"
            value={(design.channel_width ?? '').toString()}
            onValueChange={(v) => updateField('channel_width', v ? Number(v) : null)}
            size="sm"
            classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>in</span>}
          />
          <div className="flex items-center gap-2 h-10">
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Channel Mode:</span>
            <Switch
              size="sm"
              isSelected={isManualChannels}
              onValueChange={(checked) => {
                if (checked) {
                  updateField('channel_count_override', Math.max(1, autoChannelCount));
                } else {
                  updateField('channel_count_override', null);
                }
              }}
            >
              <span className="text-xs">{isManualChannels ? 'Manual' : 'Auto'}</span>
            </Switch>
          </div>
          {isManualChannels ? (
            <Input
              label="Channel Count"
              type="number"
              value={(design.channel_count_override ?? '').toString()}
              onValueChange={(v) => {
                if (v === '') {
                  updateField('channel_count_override', null);
                  return;
                }
                const n = Math.floor(Number(v));
                if (Number.isFinite(n) && n >= 1) {
                  updateField('channel_count_override', n);
                }
              }}
              size="sm"
              min={1}
              classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            />
          ) : (
            <div>
              <div className="text-[10px] mb-1" style={{ color: 'var(--text-muted)' }}>Auto Channels</div>
              <div className="text-xs font-semibold px-2 py-1.5 rounded" style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--text-primary)' }}>
                {autoChannelCount}
              </div>
            </div>
          )}
        </div>
      )}

      {isButton && (
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Button Tuftings per Foot"
            type="number"
            value={(design.button_tuftings_per_foot ?? '').toString()}
            onValueChange={(v) =>
              updateField('button_tuftings_per_foot', v ? Math.max(0, Math.floor(Number(v))) : null)
            }
            size="sm"
            classNames={{ inputWrapper: 'bg-[var(--bg-panel)]' }}
            endContent={<span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>/ft</span>}
          />
          <Autocomplete
            label="Button Hardware"
            selectedKey={design.button_hardware_id ?? undefined}
            onSelectionChange={(key) => updateField('button_hardware_id', (key as string) || null)}
            size="sm"
            placeholder="Select hardware..."
            inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
          >
            {hardwareOptions.map((h) => (
              <AutocompleteItem key={h.id}>{h.name}</AutocompleteItem>
            ))}
          </Autocomplete>
        </div>
      )}

      {isTuftedChannel && (
        <Autocomplete
          label="Wire Hardware"
          selectedKey={design.wire_hardware_id ?? undefined}
          onSelectionChange={(key) => updateField('wire_hardware_id', (key as string) || null)}
          size="sm"
          placeholder="Select wire hardware..."
          inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-panel)]' } }}
        >
          {hardwareOptions.map((h) => (
            <AutocompleteItem key={h.id}>{h.name}</AutocompleteItem>
          ))}
        </Autocomplete>
      )}

      {isChannel && effectiveChannelCount > 0 && (
        <div className="text-[10px] px-2 py-1 rounded" style={{ backgroundColor: 'var(--bg-surface)', color: 'var(--text-secondary)' }}>
          {effectiveChannelCount} channel{effectiveChannelCount !== 1 ? 's' : ''} at{' '}
          {design.channel_width ?? 0}" width
          {isManualChannels ? ' (manual override)' : ' (auto-calculated)'}
        </div>
      )}
    </div>
  );
}
